import LegalHeader from "@/components/landing/LegalHeader";
import Footer from "@/components/landing/Footer";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const ALLOWED = [
  "Spiegare al proprietario le informazioni e le analisi già raccolte dal percorso ValutaGenio",
  "Contestualizzare il valore di riferimento indicato da ValutaPro alla luce delle caratteristiche specifiche dell'immobile",
  "Approfondire obiettivi, tempi e priorità del proprietario per orientare la decisione",
  "Rispondere a domande sul mercato della zona sulla base della propria esperienza",
  "Proporre una visita o un approfondimento tecnico se ritenuto utile dal proprietario",
];

const NOT_ALLOWED = [
  "Esercitare pressione commerciale durante la fase di orientamento",
  "Presentare l'analisi ValutaGenio come una perizia, una certificazione o una valutazione garantita",
  "Fare promesse su prezzo finale, risultato o tempi di vendita",
  "Utilizzare i dati del lead per finalità diverse dal confronto richiesto",
  "Ricontattare il lead in modo insistente senza che lui lo abbia richiesto",
  "Svolgere attività professionali riservate senza le qualifiche e le abilitazioni necessarie",
];

export default function PolicyPropertyAdvisor() {
  return (
    <main data-testid="policy-page" className="bg-white text-[#0A0A0A]">
      <LegalHeader />

      <section className="max-w-[860px] mx-auto px-6 md:px-8 py-16 md:py-24">
        <span className="inline-block text-xs uppercase tracking-[0.2em] font-semibold text-[#B40101] mb-4">
          Codice di condotta
        </span>
        <h1 className="font-display font-bold text-[#0A0A0A] text-4xl md:text-5xl tracking-tight leading-tight">
          Codice di Condotta <br className="hidden md:block" />
          Property Advisor ValutaGenio
        </h1>
        <p className="mt-4 text-[#0A0A0A]/60 text-sm">
          Ultimo aggiornamento: 23 agosto 2026
        </p>

        <div className="mt-10 space-y-12 text-[#0A0A0A]/85 text-[16px] md:text-[17px] leading-relaxed">
          {/* Intro / Ruolo */}
          <section data-testid="policy-section-ruolo">
            <h2 className="font-display font-bold text-[#0A0A0A] text-2xl md:text-3xl tracking-tight mb-4">
              Il ruolo del Property Advisor
            </h2>
            <p>
              Il Property Advisor è un{" "}
              <strong>professionista indipendente</strong> del settore
              immobiliare presente sulla piattaforma ValutaGenio. Il suo primo
              confronto con il proprietario ha{" "}
              <strong>funzione orientativa</strong>: parte dalle informazioni
              già raccolte dal percorso ValutaGenio, le contestualizza e aiuta
              il proprietario ad approfondire ciò che è utile per la sua
              decisione.
            </p>
            <p className="mt-4">
              L'eventuale rapporto professionale o commerciale che potrà
              nascere in seguito è{" "}
              <strong>
                definito direttamente tra proprietario e professionista
              </strong>
              , sulla base delle qualifiche del PA, dei servizi offerti e delle
              condizioni concordate. ValutaGenio non è datore di lavoro né
              mandante del Property Advisor.
            </p>
            <p className="mt-4">
              Durante l'orientamento non è ammessa alcuna forma di pressione
              commerciale. Le attività professionali riservate restano soggette
              alle qualifiche e alle abilitazioni previste per ciascuna
              professione.
            </p>
          </section>

          {/* Cosa è consentito */}
          <section data-testid="policy-section-consentito">
            <h2 className="font-display font-bold text-[#0A0A0A] text-2xl md:text-3xl tracking-tight mb-5">
              Cosa è consentito
            </h2>
            <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-6 md:p-7">
              <ul className="space-y-3">
                {ALLOWED.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[#0A0A0A]"
                  >
                    <CheckCircle2 className="w-5 h-5 mt-0.5 text-emerald-600 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Cosa NON è consentito */}
          <section data-testid="policy-section-non-consentito">
            <h2 className="font-display font-bold text-[#0A0A0A] text-2xl md:text-3xl tracking-tight mb-5">
              Cosa non è consentito
            </h2>
            <div className="bg-[#B40101]/5 border border-[#B40101]/25 rounded-xl p-6 md:p-7">
              <ul className="space-y-3">
                {NOT_ALLOWED.map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-[#0A0A0A]"
                  >
                    <XCircle className="w-5 h-5 mt-0.5 text-[#B40101] flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* Uso dei dati */}
          <section data-testid="policy-section-dati">
            <h2 className="font-display font-bold text-[#0A0A0A] text-2xl md:text-3xl tracking-tight mb-4">
              Uso corretto dei dati del lead
            </h2>
            <p>
              Le informazioni raccolte dal percorso ValutaGenio possono essere
              utilizzate esclusivamente per rendere più utile il confronto
              richiesto dal proprietario. Non è ammesso utilizzarle per
              finalità diverse, condividerle con terzi non necessari o
              trattarle in modo non conforme alla normativa applicabile.
            </p>
          </section>

          {/* Segnalazioni */}
          <section data-testid="policy-section-sanzioni">
            <h2 className="font-display font-bold text-[#0A0A0A] text-2xl md:text-3xl tracking-tight mb-5">
              Segnalazioni
            </h2>

            <div
              data-testid="policy-sanction-flow"
              className="bg-white rounded-xl border border-[#E5E5E5] vg-card-shadow p-6 md:p-7"
            >
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 mt-1 text-[#B40101] flex-shrink-0" />
                <p className="text-[#0A0A0A]/85 leading-relaxed">
                  ValutaGenio verifica le segnalazioni ricevute e, in base alla
                  gravità o alla recidiva, può{" "}
                  <strong>ammonire, sospendere o chiudere l'account</strong>{" "}
                  del Property Advisor coinvolto.
                </p>
              </div>
            </div>

            <div
              data-testid="policy-trasparenza"
              className="mt-6 bg-[#F5F5F5] border-l-4 border-[#B40101] rounded-xl px-6 py-5"
            >
              <p className="text-[#0A0A0A]/85 leading-relaxed">
                <strong>Ogni segnalazione viene verificata</strong> prima di
                qualsiasi azione. Il processo è{" "}
                <strong>trasparente per entrambe le parti</strong>.
              </p>
            </div>
          </section>

          {/* Contatto */}
          <section data-testid="policy-section-contatti" className="pt-2">
            <div className="bg-[#F5F5F5] rounded-xl p-6 md:p-8 border border-[#E5E5E5]">
              <h3 className="font-display font-bold text-[#0A0A0A] text-xl tracking-tight mb-2">
                Hai domande sul codice di condotta?
              </h3>
              <p className="text-[#0A0A0A]/75">
                Scrivici a{" "}
                <a
                  href="mailto:info@valutagenio.com"
                  className="text-[#B40101] font-semibold hover:underline"
                >
                  info@valutagenio.com
                </a>
                .
              </p>
            </div>
          </section>
        </div>
      </section>

      <Footer />
    </main>
  );
}
